/**
 * Download what a grid is showing, as CSV or Excel.
 *
 * The server lays the file out (POST /api/export) - one route for every
 * table, so a workbook from the pipeline and one from Deals cannot drift
 * apart the way the hand-written exports did. It is sent the rows rather
 * than a query, so the file is the view: the tab, the filters, the search,
 * the sort and the visible columns in their order, every page of it.
 *
 * Without a server - offline, or while the route does not exist yet - a CSV
 * is built here from the same rows. Excel is not: a workbook is the server's
 * job, and a CSV renamed .xlsx is a file Excel refuses to open.
 */
import api from '../../lib/api.js';
import { download } from '../../lib/format.js';
import { isOffline } from '../../lib/storage.js';
import { isoDay } from './filters.js';
import { toCSV } from './csv.js';

export const FORMATS = {
  csv: { ext: 'csv', label: 'CSV' },
  xlsx: { ext: 'xlsx', label: 'Excel' }
};

/** The type a file cares about: dates and numbers get their own cells, the rest is text. */
export const exportType = type => (type === 'number' || type === 'date' ? type : 'text');

/**
 * May this person take a copy? The server's rule, mirrored so the menu is
 * not offered to someone it will refuse: the Owner always, and otherwise any
 * role that may delete something somewhere (coarseFor in backend/src/access.js
 * pitches export with delete, not with view). `menus` is null offline and
 * before /auth/me answers, where the answer is yes, as it is for can().
 */
export function exportAllowed(me, menus) {
  if (!menus) return true;
  if (me && me.role === 'Owner') return true;
  return Object.values(menus).some(verbs => (verbs || []).includes('delete'));
}

/**
 * Make the file and hand it to the browser.
 *
 * `columns` is [{ key, label, type }] and `rows` is [{ [key]: value }].
 * Resolves with 'server' or 'browser' to say who built it; rejects with an
 * Error whose message can go straight into a toast.
 */
export async function exportView({ format, name, sheet, columns, rows }) {
  const kind = FORMATS[format] || FORMATS.csv;
  const filename = `${name || 'export'}-${isoDay(new Date())}.${kind.ext}`;

  const inBrowser = () => {
    download(new Blob([toCSV(columns, rows)], { type: 'text/csv;charset=utf-8' }), filename);
    return 'browser';
  };

  if (isOffline()) {
    if (format !== 'csv') throw new Error('Excel needs the server — download a CSV instead');
    return inBrowser();
  }

  try {
    const blob = await api.exportTable({ format: kind.ext, filename, sheet: sheet || name, columns, rows });
    download(blob, filename);
    return 'server';
  } catch (e) {
    /*
     * Only a missing route falls back. A 403 is the server saying this
     * person may not export, and building the file here anyway would be
     * walking round that answer; anything else is a real failure worth
     * reporting rather than papering over.
     */
    if (e && e.status === 404) {
      if (format === 'csv') return inBrowser();
      throw new Error('This server cannot make Excel files yet — download a CSV instead');
    }
    throw new Error((e && e.message) || 'That export could not be made');
  }
}
